import create, { UseStore } from 'zustand';

import { fetchRequest } from '../';
import { visitDrawerTabs } from '../../components/coding-tool/coding-components';
import { Alert, Worksheet, WorksheetComponents } from '../domain';
import { socketRequest } from '../socket';
import { truCode } from '../trucode';
import { WorksheetResult, worksheetMessage } from '../worksheet-service';
import { normalizeServiceDates } from '../worksheets';

type MessageResult = WorksheetResult<Alert[]>;
type AlertApiProps = {
  worksheetId?: string;
  alerts?: Alert[];
  deferConnection?: boolean;
};

export type AlertApi = {
  alerts: Alert[];
  openAlert: Alert;
  openEdit(alert: Alert): void;
  acknowledgeAlert(alert: Alert): void;
  overrideAlert(alert: Alert, overrideReason: string): void;
  close(): void;
  connect(): void;
};

export type AlertStore = UseStore<AlertApi>;
const socketsEnabled = true;

type AlertResult = WorksheetResult<Alert>;
export const acknowledgeAlertRequest = fetchRequest<Alert, AlertResult>({
  method: 'post',
  body: (args: Alert) => worksheetMessage(args, 'acknowledgeAlert'),
});

export const overrideAlertRequest = fetchRequest<Alert, AlertResult>({
  method: 'post',
  body: (args: Alert) => worksheetMessage(args, 'overrideAlert'),
});

export function unresolvedAlerts(list: Alert[]): Alert[] {
  return (list || []).filter((a) => (a.alertType !== 'SYSTEMERROR' && a.alertType !== 'TRUCODEEDIT') &&  !(!a.resolved || !a.overridden || !a.acknowledged ));
}

export function alertApi(props?: AlertApiProps): AlertStore {
  const { worksheetId, alerts, deferConnection } = props || {};

  let close = () => null;
  let connect = () => null;

  const api = create<AlertApi>((set, get) => ({
    alerts: alerts || [],
    openAlert: null,
    openEdit: (alert: Alert) => {
      visitDrawerTabs.setState({ index: 2 });
      truCode.setState({ alert });
    },
    acknowledgeAlert: async (alert: Alert) => {
      alert.acknowledged = true;

      await acknowledgeAlertRequest(alert);
      set({ alerts: get().alerts.slice() });
    },
    overrideAlert: async (alert: Alert) => {
      alert.overridden = true;
      const { worksheetId, alertId, overrideReason } = alert;
      await overrideAlertRequest({
        worksheetId,
        alertId,
        overrideReason,
      });
      set({ alerts: get().alerts.slice() });
    },
    close: () => close(),
    connect: () => connect(),
  }));

  if (worksheetId && socketsEnabled) {
    const messages = socketRequest({
      worksheetId,
      deferConnection,
      onSuccess: (data: MessageResult) => {
        const { result } = data;
        if (result && data.action !== 'checkconnection') {
          // console.log('socket result', data);
          //TODO filter
        //  api.setState({ alerts: result });
        }
      },
    });
    close = () => messages.getState().close();
    connect = () => messages.getState().connect();
  }

  return api;
}

export const saveAndValidateWorksheetRequest = fetchRequest<Worksheet, WorksheetResult<WorksheetComponents>>({
  method: 'post',
  body: (args: Worksheet) => worksheetMessage(normalizeServiceDates({ ...args }), 'saveandvalidateworksheet'),
});

function getDisplayableAlerts(component: WorksheetComponents): Alert[] {
  //TODO possible alert filters
  if (component && component.alerts) {
    return component.alerts;
  }
  return [];
}

async function worksheetAlerts(sheet: Worksheet, onResult: (a: WorksheetComponents) => void): Promise<Alert[]> {
  try {
    let result: WorksheetResult<WorksheetComponents> = await saveAndValidateWorksheetRequest(sheet);

    if (!result.success) {
      console.error(result);
      return Promise.reject((result?.errors || ['Validation Error']).join('. '));
    }

    const component = result.result;
    onResult(component);
    let alertList: Alert[] = getDisplayableAlerts(component);

    return Promise.resolve(alertList || []);
  } catch (error) {
    console.error(error);
    return Promise.resolve([]);
  }
}

export function worksheetAlertUpdate(
  store: AlertStore,
  onError: (error: string) => void,
  onComplete?: (hasAlerts?: boolean) => void,
  onResult?: (a: WorksheetComponents) => void
): (sheet: Worksheet) => void {
  return async (sheet) => {
    try {
      const alerts = await worksheetAlerts(sheet, (c) => {
        if (onResult) {
          onResult(c);
        }
      });
      store.setState({ alerts });
      if (onComplete) {
        let hasAlerts = alerts?.length && alerts.filter((a) => !a.resolved).length > 0;
        onComplete(hasAlerts);
      }
    } catch (error) {
      onError(error);
    }
  checkAlertStatus();
  };
}



export const alertsUpdated = create<{b:boolean}>(() => ({
  b: true
}));


export function checkAlertStatus(){
  alertsUpdated.setState({b: !alertsUpdated.getState().b});
}
